import { DFSValueType, BooleanExpression, DFSExpression, MobileCardDefinition, MobileLayoutNode, MobileViewDefinition, parseFieldIdentifier, TableDefinition, TableRowDefinition, DFSPrimitiveType } from '../services';
import { rescopeDataPath } from '.';
import { useState, RefObject, useEffect, useContext, useLayoutEffect, useRef, useCallback } from 'react';
import { switchMap, map, distinct, count, distinctUntilChanged, tap } from 'rxjs/operators';
import { combineLatest, of, from, Observable, Subscription } from 'rxjs';
import equal from "fast-deep-equal";
import { ErrorObject } from 'ajv';
import fast_deep_equal from 'fast-deep-equal';

import { VISIBILITY_PATH_STARTING_INDEX } from '../constants';
import { translateErrors } from './translate';
import { FormContext } from '../FormContext';

export const useDataValue = function<T extends DFSValueType>(dataPath: string): T | undefined {
  const [ currentValue, setCurrentValue ] = useState(undefined);
  const { expMgr, scope } = useContext(FormContext);

  // consider useMemo
  useEffect(function r() {
    const t = expMgr!.watch(dataPath, scope)
      .subscribe(function(e: any) {
        setCurrentValue(e);
      });

    return function() {
      return t.unsubscribe();
    }
  }, [dataPath, expMgr, scope]);

  return currentValue;
}

export const useExpressionValue = function<T extends DFSValueType>(expression: DFSExpression | undefined): T | undefined {
  const { expMgr, scope } = useContext(FormContext);

  const exp$ = expMgr!.watch(expression, scope);
  let initialValue: T | undefined;

  exp$.subscribe(function(e: any) {
    return initialValue = e;
  }).unsubscribe();

  // strange async setter
  // let [ currentValue, setCurrentValue ] = useState(undefined);
  let [ currentValue, setCurrentValue ] = useState(initialValue);

  useEffect(function e() {
    // don't know about this distinctuntilchanged
    const r = exp$
      .pipe(distinctUntilChanged((a, b) => equal(a, b)))
      .subscribe(function(e: any) {
        setCurrentValue(e);
      });

    return function() {
      return r.unsubscribe();
    }
  }, [exp$]);

  return currentValue;
}

// i init as false instead of null
export const useVisibility = function(path: string): boolean {
  const [ isVisible, setIsVisible ] = useState<boolean>(false);
  const  { visibilityMgr } = useContext(FormContext);

  useEffect(function r() {
    const subscription = visibilityMgr!.watch(path.slice(VISIBILITY_PATH_STARTING_INDEX))
      .subscribe((visibility: any) => {
        setIsVisible(visibility);
      });

    return function() {
      return subscription.unsubscribe();
    }
  }, [path, visibilityMgr]);

  return isVisible;
}

export const useImplicitVisibility = function(definition: any): boolean {
  const [ isVisible, setIsVisible ] = useState(true);
  const { expMgr, scope } = useContext(FormContext);

  useEffect(function r() {
    const visibilityConditions = definition.children.map(function(e: any) {
      return e.visibility ?? e.visible;
    });

    const isStatic = visibilityConditions.indexOf(undefined);
    let visibility$;

    if (isStatic >= 0) {
      visibility$ = of(true);
    } else {
      visibility$ = combineLatest(visibilityConditions.map((exp: any) => expMgr!.watch(exp, scope)))
        .pipe(
          map((e: any) => e.indexOf(true) >= 0),
          distinctUntilChanged()
        )
    }

    const subscription = visibility$.subscribe(function(e: any) {
      return setIsVisible(e);
    });

    return function() {
      return subscription.unsubscribe()
    }
  }, [definition, expMgr, scope]);

  return isVisible;
}

/**
 * **Don't use this until it is fixed.** Right now this will be called endlessly until a
 * reference to the target element exists, which may not happen for a long time in normal app use.
 */
export const useSize = function<T extends HTMLElement>(ref?: RefObject<T>): number[] {
  let [t, n] = useState([0, 0]);
  
  const u = useCallback(() => {
    if (ref) {
      if (ref.current) {
        n([ref.current.clientWidth, ref.current.clientHeight])
      } else {
        setTimeout(u, 1e3)
      }
    } else {
      n([window.innerWidth, window.innerHeight])
    }
  }, [ref])

  useLayoutEffect(function() {
    window.addEventListener('resize', u);
    u();
    return function() {
      return window.removeEventListener('resize', u)
    }
  }, [u]);
  useEffect(function() {
    u()
  }, [ref, u]);
  return t
}

// typings
function walkLayoutNode(node: any, path: string, visitor: (n: any, p: string) => any) {
  visitor(node, path);
  if (node.cards) {
    node.cards.forEach(function(childNode: any, idx: string) {
      walkLayoutNode(childNode, path + '/cards/' + idx, visitor)
    })
  } else if (node.child) {
    walkLayoutNode(node.child, path.concat('/child'), visitor)
  } else if (node.content) {
    walkLayoutNode(node.content, path.concat('/content'), visitor)
  } else if (node.children) {
    node.children.forEach(function(childNode: any, idx: string) {
      walkLayoutNode(childNode, path + '/children/' + idx, visitor)
    })
    // radio and multicheckbox 'Other' fields aren't part of a separate layout node
  } else if (node.rows) {
    node.rows.forEach(function(childNode: any, idx: string) {
      walkLayoutNode(childNode, path + '/rows/' + idx, visitor)
    })
  } else if ((node.type === 'MultiCheckbox' || node.type === 'Radio') && node.options?.includes('Other')) {
    const dataPath = '{' + parseFieldIdentifier(node.dataPath) + 'Other}';
    visitor({...node, dataPath}, dataPath);
  }
}

// remove this switchmap
// this is hard on performance
// todo just sub to this in the component? and set error count?
export const useLayoutNodeErrorCount = function(path: string, node: MobileLayoutNode): number {
  let [ errors, setErrors ] = useState(0);
  const { validityMgr } = useContext(FormContext);

  useEffect(function t() {
    const validityWatchers: Observable<any>[] = [];

    walkLayoutNode(node, path, function(node: any) {
      if (node.dataPath) {
        validityWatchers.push(validityMgr!.watch(parseFieldIdentifier(node.dataPath)));
      }
    });

    const subscription = combineLatest(validityWatchers)
      .pipe(
        map(function(combinedErrors: ErrorObject[][]) {
          return combinedErrors.reduce(function(acc: ErrorObject[], curr: ErrorObject[]) {
            return [...acc, ...curr];
          }, [])
        }),
        distinctUntilChanged((a, b) => fast_deep_equal(a, b)),
        switchMap(function(e: ErrorObject[]) {
          return from(e)
            .pipe(
              // map(function(e: ErrorObject) {
              map(function(e: any) {
                return '' + e.dataPath + (e.keyword === 'required' ? '-' + e.params.missingProperty : '')
              }),
              distinct(),
              count()
            )
          }),
        )

      .subscribe(function(errorCount: any) {
        return setErrors(errorCount);
      });

      return function() {
        return subscription.unsubscribe();
      }
    }, [node, path, validityMgr]);

  return errors;
}

// export const useLayoutNodeErrorCount = (path: string, node: MobileLayoutNode) => {
//   const [ errors, setErrors ] = useState(0);
//   const { validityMgr } = useContext(FormContext);

//   // var t = __read(useState(0), 2),
//   //     n = t[0],
//   //     u = t[1];
//   // var i = react_1.useContext(FormContext_1.FormContext).validityMgr;
//   useEffect(function t() {
//       const validityWatchers: Observable<any>[] = [];
//       walkLayoutNode(node, path, function(next) {
//         if (next.dataPath) {
//           validityWatchers.push(validityMgr!.watch(parseFieldIdentifier(next.dataPath)))
//         }
//       });
//       console.log('validity watchers:', validityWatchers);
//       const subscription = combineLatest(validityWatchers)
//         .pipe(map(function(e: ErrorObject[][]) {
//           console.log('all errors:', e);
//           return e.reduce(function(acc: ErrorObject[], curr: ErrorObject[]) {
//               return [...acc, ...curr];
//           }, [])
//       }), switchMap(function(errors: ErrorObject[]) {
//         return from(errors).pipe(map(function(e) {
//           return "" + e.dataPath + (e.keyword === "required" ? "-" + (e.params as any).missingProperty : "")
//         }), distinct(), count())
//       })).subscribe(function(e) {
//         return setErrors(e)
//       });
//       return function() {
//         return subscription.unsubscribe()
//       }
//   }, [node, path]);
  
//   return errors;
// }

export const useForceRedraw = function(dependencies: any[]): boolean {
  const [isRedrawing, setIsRedrawing] = useState(true);

  useEffect(function() {
    setIsRedrawing(true);
  }, [...dependencies]);

  useEffect(function() {
    if (isRedrawing) {
      setIsRedrawing(false);
    }
  }, [isRedrawing]);

  return isRedrawing
}

export const useDataFieldValidity = function(path?: string): string {
  const [ error, setError ] = useState<ErrorObject[] | null>(null);
  const { validityMgr, scope } = useContext(FormContext);

  // todo just return subject and share it, unsub in component?
  useEffect(function watchValidity() {
    if (!path) {
      return;
    }
    const targetPath = parseFieldIdentifier(rescopeDataPath(path, scope));

    const subscription = validityMgr!.watch(targetPath)
      .pipe(distinctUntilChanged())
      .subscribe(function(errors: ErrorObject[]) {
        return setError(translateErrors(errors, targetPath) as unknown as ErrorObject[])
      });
  
    return function() {
      return subscription.unsubscribe()
    }

  }, [path]);

  return error as unknown as string;
}

export const useEditability = function(editability?: BooleanExpression): boolean {
  const { isReadonly } = useContext(FormContext);
  const editable = useExpressionValue(editability === undefined ? true : editability) as boolean;
  return !isReadonly && editable;
}

export const usePrevious = (value: any, initialValue: any) => {
  const ref = useRef(initialValue);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};
export const useEffectDebugger = (effectHook: any, dependencies: any, dependencyNames = []) => {
  const previousDeps = usePrevious(dependencies, []);

  const changedDeps = dependencies.reduce((accum: any, dependency: any, index: any) => {
    if (dependency !== previousDeps[index]) {
      const keyName = dependencyNames[index] || index;
      return {
        ...accum,
        [keyName]: {
          before: previousDeps[index],
          after: dependency
        }
      };
    }

    return accum;
  }, {});

  if (Object.keys(changedDeps).length) {
    console.log('[use-effect-debugger] ', changedDeps);
  }

  useEffect(effectHook, dependencies);
};